import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'

import {
  ALL_PAGE_HEADER_BLOCKS_QUERY,
  SINGLE_PAGE_HEADER_BLOCK_QUERY,
  CREATE_PAGE_HEADER_BLOCK_MUTATION,
  UPDATE_PAGE_HEADER_BLOCK_MUTATION,
  DELETE_PAGE_HEADER_BLOCK_MUTATION,
  FORCE_DELETE_PAGE_HEADER_BLOCK_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const cmsPageHeaderBlockRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_PAGE_HEADER_BLOCK_QUERY, variables).then((data: any) => {
      return hydrateObject(data.pageHeaderBlock)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_PAGE_HEADER_BLOCKS_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.pageHeaderBlocks)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_PAGE_HEADER_BLOCK_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.pageHeaderBlock),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_PAGE_HEADER_BLOCKS_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.pageHeaderBlocks),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_PAGE_HEADER_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.pageHeaderBlock)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_PAGE_HEADER_BLOCK_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.pageHeaderBlock)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_PAGE_HEADER_BLOCK_MUTATION, variables).then((data: any) => {
      return data.deleteCmsPageHeaderBlock
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_PAGE_HEADER_BLOCK_MUTATION, variables).then((data: any) => {
      return data.forceDeleteCmsPageHeaderBlock
    })
  },
}

export default cmsPageHeaderBlockRepository
